import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit2, Check, X } from 'lucide-react';
import dayjs from 'dayjs';
import { fetchClient, fetchClientBookings, updateClient } from '../../lib/bookingsApi';

const STATUS_COLOR = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  pending:   'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show:  'bg-slate-100 text-slate-600',
};

function EditableField({ label, value, onSave, type = 'text' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  const save = () => { onSave(draft); setEditing(false); };
  const cancel = () => { setDraft(value || ''); setEditing(false); };

  return (
    <div className="py-2.5 border-b border-surface-border last:border-0">
      <p className="text-xs text-ink-muted mb-1">{label}</p>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type={type}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="input flex-1 py-1 text-sm"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
          />
          <button onClick={save}   className="p-1 rounded text-emerald-600 hover:bg-emerald-50"><Check size={14} /></button>
          <button onClick={cancel} className="p-1 rounded text-ink-muted hover:bg-surface-100"><X size={14} /></button>
        </div>
      ) : (
        <div className="flex items-center justify-between group">
          <p className="text-sm text-ink font-medium">{value || <span className="text-ink-muted italic">—</span>}</p>
          <button
            onClick={() => setEditing(true)}
            className="opacity-0 group-hover:opacity-100 p-1 rounded text-ink-muted hover:bg-surface-100 transition-all"
          >
            <Edit2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: () => fetchClient(id),
  });

  const { data: bookings = [] } = useQuery({
    queryKey: ['client-bookings', id],
    queryFn: () => fetchClientBookings(id),
    enabled: !!client,
  });

  const mutation = useMutation({
    mutationFn: (updates) => updateClient(id, { ...client, ...updates }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client', id] }),
  });

  if (isLoading) return <div className="p-6 text-ink-muted text-sm">Loading…</div>;
  if (!client)   return <div className="p-6 text-ink-muted text-sm">Client not found.</div>;

  const totalSpend = bookings
    .filter(b => b.status === 'completed')
    .reduce((sum, b) => sum + (b.Service?.price_cents || 0), 0);

  return (
    <div className="p-6 max-w-3xl">
      <button
        onClick={() => navigate('/clients')}
        className="flex items-center gap-1.5 text-sm text-ink-secondary hover:text-ink mb-5 transition-colors"
      >
        <ArrowLeft size={14} /> Back to clients
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-lg">
          {client.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="text-xl font-semibold text-ink">{client.name}</h1>
          <p className="text-sm text-ink-secondary">Client since {dayjs(client.created_at).format('MMM YYYY')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total visits',    value: bookings.filter(b => b.status === 'completed').length },
          { label: 'Total bookings',  value: bookings.length },
          { label: 'Total spend',     value: `$${(totalSpend / 100).toFixed(2)}` },
        ].map(s => (
          <div key={s.label} className="card p-4 text-center">
            <p className="text-2xl font-semibold text-ink">{s.value}</p>
            <p className="text-xs text-ink-muted mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Profile */}
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-ink mb-2">Contact info</h2>
          <EditableField label="Name"  value={client.name}  onSave={v => mutation.mutate({ name: v })} />
          <EditableField label="Email" value={client.email} onSave={v => mutation.mutate({ email: v })} type="email" />
          <EditableField label="Phone" value={client.phone} onSave={v => mutation.mutate({ phone: v })} type="tel" />
          <EditableField label="Notes" value={client.notes} onSave={v => mutation.mutate({ notes: v })} />
        </div>

        {/* Upcoming */}
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-ink mb-2">Upcoming</h2>
          {bookings.filter(b => b.status === 'confirmed' && new Date(b.start_at) > new Date()).length === 0 ? (
            <p className="text-xs text-ink-muted">No upcoming appointments.</p>
          ) : (
            <ul className="space-y-2">
              {bookings
                .filter(b => b.status === 'confirmed' && new Date(b.start_at) > new Date())
                .slice(0, 3)
                .map(b => (
                  <li key={b.id} className="text-xs text-ink-secondary">
                    <p className="font-medium text-ink">{b.Service?.name}</p>
                    <p>{dayjs(b.start_at).format('MMM D [at] h:mm A')} · {b.Staff?.name}</p>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>

      {/* Booking history */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-border">
          <h2 className="text-sm font-semibold text-ink">Booking history</h2>
        </div>
        {!bookings.length ? (
          <div className="p-6 text-center text-ink-muted text-sm">No bookings yet.</div>
        ) : (
          <ul className="divide-y divide-surface-border">
            {bookings.map(b => (
              <li key={b.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div>
                  <p className="font-medium text-ink">{b.Service?.name}</p>
                  <p className="text-xs text-ink-muted">
                    {dayjs(b.start_at).format('MMM D, YYYY [·] h:mm A')} · {b.Staff?.name}
                  </p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[b.status] || ''}`}>
                  {b.status?.replace('_', '-')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
